import React, { useState, useRef } from 'react';
import { PDFDocument } from 'pdf-lib';

// Certificate types configuration
const CERTIFICATE_TYPES = [
  {
    id: 'stop_the_bleed',
    name: 'Stop The Bleed',
    icon: '🩸',
    color: 'bg-red-500',
    hoverColor: 'hover:bg-red-600',
    borderColor: 'border-red-500',
    available: true,
    templatePath: '/certificate_stop_the_bleed_blank.pdf',
    filenamePrefix: 'stop_the_bleed',
    defaultSubject: 'Your Stop the Bleed Certificate',
    defaultBody: `Hello {name},

Congratulations on completing the Stop the Bleed course! Please find your certificate attached.

Thank you for taking the time to learn these life-saving skills.

Best regards,
[Your Name]`
  },
  {
    id: 'paramedic_graduation',
    name: 'Paramedic Graduation',
    icon: '🎓',
    color: 'bg-blue-500',
    hoverColor: 'hover:bg-blue-600',
    borderColor: 'border-blue-500',
    available: false,
    templatePath: null,
    filenamePrefix: 'paramedic_graduation',
    defaultSubject: 'Your Paramedic Graduation Certificate',
    defaultBody: `Hello {name},

Congratulations on your graduation from the Paramedic program!

Best regards,
[Your Name]`
  },
  {
    id: 'bls_provider',
    name: 'BLS Provider',
    icon: '❤️',
    color: 'bg-pink-500',
    hoverColor: 'hover:bg-pink-600',
    borderColor: 'border-pink-500',
    available: false,
    templatePath: null,
    filenamePrefix: 'bls_provider',
    defaultSubject: 'Your BLS Provider Certificate',
    defaultBody: `Hello {name},

Congratulations on completing your BLS Provider certification!

Best regards,
[Your Name]`
  }
];

const BatchTool = () => {
  const [selectedCertType, setSelectedCertType] = useState(CERTIFICATE_TYPES[0]);
  const [studentNames, setStudentNames] = useState('');
  const [studentEmails, setStudentEmails] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatedPdfs, setGeneratedPdfs] = useState([]);
  const [showEmailHelper, setShowEmailHelper] = useState(false);
  const [emailSubject, setEmailSubject] = useState(CERTIFICATE_TYPES[0].defaultSubject);
  const [emailBody, setEmailBody] = useState(CERTIFICATE_TYPES[0].defaultBody);
  const [showMailMergeTip, setShowMailMergeTip] = useState(false);
  const [showVbaTip, setShowVbaTip] = useState(false);
  const fileInputRef = useRef(null);

  // Handle certificate type selection
  const handleCertTypeSelect = (certType) => {
    if (!certType.available) return;
    setSelectedCertType(certType);
    setEmailSubject(certType.defaultSubject);
    setEmailBody(certType.defaultBody);
    // Clear generated PDFs when switching types
    setGeneratedPdfs([]);
    setPreviewUrl(null);
  };

  // Format date for display on certificate
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date parts directly to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Parse student names from textarea (one per line)
  const parseStudentNames = () => {
    return studentNames
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);
  };

  // Parse student emails from textarea (one per line)
  const parseStudentEmails = () => {
    return studentEmails
      .split('\n')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Generate proper filename based on selected certificate type
  const generateFilename = (fullName) => {
    const nameParts = fullName.trim().split(/\s+/);
    const formattedName = nameParts.join('_').toLowerCase();
    return `${selectedCertType.filenamePrefix}_${formattedName}.pdf`;
  };

  // Generate a single certificate PDF using form fields
  const generateCertificate = async (studentName, dateText) => {
    try {
      const templateUrl = selectedCertType.templatePath;
      const templateBytes = await fetch(templateUrl).then(res => res.arrayBuffer());
      
      const pdfDoc = await PDFDocument.load(templateBytes);
      const form = pdfDoc.getForm();
      
      try {
        const nameField = form.getTextField('Text Field 3');
        nameField.setText(studentName);
      } catch (e) {
        console.warn('Could not find name field:', e.message);
      }
      
      try {
        const dateField = form.getTextField('Text Field 4');
        dateField.setText(dateText);
      } catch (e) {
        console.warn('Could not find date field:', e.message);
      }
      
      form.flatten();
      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
    } catch (error) {
      console.error('Error generating certificate:', error);
      throw error;
    }
  };

  // Generate preview for first student
  const handlePreview = async () => {
    const names = parseStudentNames();
    if (names.length === 0) {
      alert('Please enter at least one student name');
      return;
    }
    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    setIsProcessing(true);
    try {
      const dateText = formatDate(selectedDate);
      const pdfBytes = await generateCertificate(names[0], dateText);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      alert('Error generating preview: ' + error.message);
    }
    setIsProcessing(false);
  };

  // Generate all certificates
  const handleGenerateAll = async () => {
    const names = parseStudentNames();
    const emails = parseStudentEmails();
    
    if (names.length === 0) {
      alert('Please enter at least one student name');
      return;
    }
    if (!selectedDate) {
      alert('Please select a date');
      return;
    }

    setIsProcessing(true);
    setGeneratedPdfs([]);
    
    try {
      const dateText = formatDate(selectedDate);
      const pdfs = [];
      
      for (let i = 0; i < names.length; i++) {
        const name = names[i];
        const email = emails[i] || '';
        const pdfBytes = await generateCertificate(name, dateText);
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfs.push({
          name: name,
          email: email,
          url: url,
          filename: generateFilename(name)
        });
      }
      
      setGeneratedPdfs(pdfs);
    } catch (error) {
      alert('Error generating certificates: ' + error.message);
    }
    setIsProcessing(false);
  };

  // Download a single PDF
  const handleDownload = (pdf) => {
    const link = document.createElement('a');
    link.href = pdf.url;
    link.download = pdf.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download all PDFs as individual files
  const handleDownloadAll = () => {
    generatedPdfs.forEach((pdf, index) => {
      setTimeout(() => {
        handleDownload(pdf);
      }, index * 500);
    });
  };

  // Import names and emails from CSV with smart column detection
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      // Handle both comma and tab-separated files
      const delimiter = text.includes('\t') ? '\t' : ',';
      const lines = text.split('\n').filter(line => line.trim().length > 0);
      
      if (lines.length === 0) return;
      
      // Parse header row to find column indices
      const headerRow = lines[0].split(delimiter).map(col => col.trim().replace(/"/g, '').toLowerCase());
      
      // Find column indices - support various common header names
      const firstNameIdx = headerRow.findIndex(h => 
        h === 'first name' || h === 'firstname' || h === 'first_name' || h === 'fname'
      );
      const lastNameIdx = headerRow.findIndex(h => 
        h === 'last name' || h === 'lastname' || h === 'last_name' || h === 'lname'
      );
      const fullNameIdx = headerRow.findIndex(h => 
        h === 'name' || h === 'full name' || h === 'fullname' || h === 'student' || h === 'student name'
      );
      const emailIdx = headerRow.findIndex(h => 
        h === 'email' || h === 'email address' || h === 'e-mail' || h === 'emailaddress'
      );
      
      const names = [];
      const emails = [];
      
      // Process data rows (skip header)
      for (let i = 1; i < lines.length; i++) {
        const columns = lines[i].split(delimiter).map(col => col.trim().replace(/"/g, ''));
        
        // Skip empty rows
        if (columns.every(c => !c)) continue;
        
        let fullName = '';
        
        // Try to build full name from First Name + Last Name columns
        if (firstNameIdx !== -1 && lastNameIdx !== -1) {
          const firstName = columns[firstNameIdx] || '';
          const lastName = columns[lastNameIdx] || '';
          fullName = `${firstName} ${lastName}`.trim();
        } 
        // Fall back to full name column
        else if (fullNameIdx !== -1) {
          fullName = columns[fullNameIdx] || '';
        }
        // Last resort: use first non-empty column that doesn't look like an ID or email
        else {
          for (const col of columns) {
            if (col && !col.includes('@') && !/^\d+$/.test(col) && col.length > 1) {
              fullName = col;
              break;
            }
          }
        }
        
        // Get email
        let email = '';
        if (emailIdx !== -1) {
          email = columns[emailIdx] || '';
        } else {
          // Find any column containing @
          email = columns.find(c => c.includes('@')) || '';
        }
        
        if (fullName) {
          names.push(fullName);
          emails.push(email);
        }
      }
      
      setStudentNames(names.join('\n'));
      setStudentEmails(emails.join('\n'));
      
      // Show import summary
      alert(`Imported ${names.length} students!\n\nDetected columns:\n• Names: ${firstNameIdx !== -1 && lastNameIdx !== -1 ? 'First Name + Last Name' : fullNameIdx !== -1 ? 'Full Name' : 'Auto-detected'}\n• Emails: ${emailIdx !== -1 ? 'Email column found' : emails.filter(e => e).length > 0 ? 'Auto-detected' : 'None found'}`);
    };
    reader.readAsText(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Generate Word Mail Merge data
  const generateMailMergeCSV = () => {
    const names = parseStudentNames();
    const emails = parseStudentEmails();
    
    let csv = 'Name,Email,Filename,FirstName\n';
    names.forEach((name, i) => {
      const firstName = name.split(' ')[0];
      csv += `"${name}","${emails[i] || ''}","${generateFilename(name)}","${firstName}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mail_merge_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate Outlook VBA script
  const generateOutlookScript = () => {
    const names = parseStudentNames();
    const emails = parseStudentEmails();
    
    let script = `' Outlook VBA Script for Batch Email with Attachments
' To use: Press Alt+F11 in Outlook, insert a new module, paste this code, and run SendBatchEmails

Sub SendBatchEmails()
    Dim OutApp As Object
    Dim OutMail As Object
    Dim i As Integer
    Dim names As Variant
    Dim emails As Variant
    Dim filenames As Variant
    Dim folderPath As String
    
    ' SET THIS TO YOUR CERTIFICATE FOLDER PATH
    folderPath = "C:\\Users\\YourName\\Downloads\\"
    
    names = Array(${names.map(n => `"${n}"`).join(', ')})
    emails = Array(${emails.map(e => `"${e}"`).join(', ')})
    filenames = Array(${names.map(n => `"${generateFilename(n)}"`).join(', ')})
    
    For i = LBound(names) To UBound(names)
        If emails(i) <> "" Then
            Set OutApp = CreateObject("Outlook.Application")
            Set OutMail = OutApp.CreateItem(0)
            
            With OutMail
                .To = emails(i)
                .Subject = "${emailSubject}"
                .Body = Replace("${emailBody.replace(/\n/g, '" & vbCrLf & "')}", "{name}", Split(names(i), " ")(0))
                .Attachments.Add folderPath & filenames(i)
                .Display  ' Change to .Send to send automatically
            End With
            
            Set OutMail = Nothing
            Set OutApp = Nothing
        End If
    Next i
    
    MsgBox "Created " & UBound(names) + 1 & " emails!", vbInformation
End Sub
`;
    
    const blob = new Blob([script], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'outlook_batch_email.bas';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copy email list for BCC
  const copyEmailsForBCC = () => {
    const emails = parseStudentEmails().filter(e => e.includes('@'));
    navigator.clipboard.writeText(emails.join('; '));
    alert(`Copied ${emails.length} emails to clipboard!\n\nNote: BCC sends same email to all - you'll need to attach all certificates or use individual emails.`);
  };

  return (
    <div className="min-h-screen bg-bg text-text p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-2 text-primary">Batch Certificate Tool</h1>
        
        {/* Certificate Type Selection */}
        <div className="bg-white rounded-lg shadow px-2 py-4 mb-6">
          <label className="block text-sm font-semibold mb-3 text-center">
            📜 Select Certificate Type
          </label>
          <div className="flex flex-wrap gap-10 justify-center">
            {CERTIFICATE_TYPES.map((certType) => (
              <button
                key={certType.id}
                onClick={() => handleCertTypeSelect(certType)}
                disabled={!certType.available}
                className={`
                  relative flex items-center gap-3 px-6 py-4 rounded-lg border-2 transition-all duration-200
                  ${selectedCertType.id === certType.id 
                    ? `${certType.color} text-white ${certType.borderColor} shadow-lg scale-105` 
                    : certType.available 
                      ? `bg-white ${certType.hoverColor} hover:text-white border-gray-200 hover:${certType.borderColor} hover:shadow-md`
                      : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  }
                `}
                title={certType.available ? certType.name : `${certType.name} - Coming Soon`}
              >
                <span className="text-2xl">{certType.icon}</span>
                <span className="font-semibold">{certType.name}</span>
                {!certType.available && (
                  <span className="absolute -top-2 -right-2 bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full">
                    Soon
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            {/* Date Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <label className="block text-sm font-semibold mb-2">
                📅 Certificate Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {selectedDate && (
                <p className="mt-2 text-sm text-gray-600">
                  Will appear as: <strong>{formatDate(selectedDate)}</strong>
                </p>
              )}
            </div>

            {/* Student Data - Side by Side Columns */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-semibold">
                  👥 Student Info
                </label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileImport}
                    ref={fileInputRef}
                    className="hidden"
                    id="file-import"
                  />
                  <label
                    htmlFor="file-import"
                    className="cursor-pointer text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded transition-colors"
                  >
                    📁 Import CSV
                  </label>
                </div>
              </div>
              
              {/* Side-by-side columns */}
              <div className="flex gap-1">
                <div className="flex-1">
                  <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-300 border-b-0 rounded-t">
                    Name
                  </div>
                  <textarea
                    value={studentNames}
                    onChange={(e) => setStudentNames(e.target.value)}
                    placeholder="John Smith&#10;Jane Doe&#10;Bob Johnson"
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-b focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none"
                  />
                </div>
                <div className="flex-1">
                  <div className="bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-300 border-b-0 rounded-t">
                    Email
                  </div>
                  <textarea
                    value={studentEmails}
                    onChange={(e) => setStudentEmails(e.target.value)}
                    placeholder="john@email.com&#10;jane@email.com&#10;bob@email.com"
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-b focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none"
                  />
                </div>
              </div>
              
              <div className="mt-2 text-sm text-gray-600 text-right">
                {parseStudentNames().length} student(s) • {parseStudentEmails().filter(e => e.includes('@')).length} email(s)
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handlePreview}
                disabled={isProcessing}
                className="flex-1 bg-blue-500 text-white font-semibold px-4 py-3 rounded-lg shadow hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isProcessing ? '⏳...' : '👁️ Preview'}
              </button>
              <button
                onClick={handleGenerateAll}
                disabled={isProcessing}
                className="flex-1 bg-green-500 text-white font-semibold px-4 py-3 rounded-lg shadow hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isProcessing ? '⏳...' : '🎓 Generate All'}
              </button>
            </div>
          </div>

          {/* Preview & Results Section */}
          <div className="space-y-6">
            {/* PDF Preview */}
            {previewUrl && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Preview</h2>
                <iframe
                  src={previewUrl}
                  className="w-full h-80 border border-gray-300 rounded"
                  title="Certificate Preview"
                />
              </div>
            )}

            {/* Generated PDFs List */}
            {generatedPdfs.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">
                    Certificates ({generatedPdfs.length})
                  </h2>
                  <button
                    onClick={handleDownloadAll}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-secondary hover:text-primary transition-colors text-sm font-semibold"
                  >
                    ⬇️ Download All
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {generatedPdfs.map((pdf, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                    >
                      <div>
                        <div className="font-medium">{pdf.name}</div>
                        <div className="text-xs text-gray-500">{pdf.filename}</div>
                      </div>
                      <button
                        onClick={() => handleDownload(pdf)}
                        className="text-blue-500 hover:text-blue-700 font-semibold text-sm"
                      >
                        ⬇️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            {!previewUrl && generatedPdfs.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-6 text-gray-600">
                <h3 className="font-semibold mb-3">📋 Instructions</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Select the certificate date</li>
                  <li>Enter student names (one per line) or import CSV</li>
                  <li>Optionally add emails for batch email tools</li>
                  <li>Click "Generate All" to create certificates</li>      
                  <li>Preview or download individual certificates</li>        
                </ol>
              </div>
            )}
          </div>

          {/* Email Tools Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-center">📧 Batch Email Tools</h2>
              </div>

              {/* Email Template */}
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">Subject Line</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-1">
                  Email Body <span className="font-normal text-gray-500">(use {'{name}'} for first name)</span>
                </label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>

              {/* Export Buttons */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={generateMailMergeCSV}
                    disabled={parseStudentNames().length === 0}
                    className="flex-1 bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    📊 Export Mail Merge CSV (for Word)
                  </button>
                  <button
                    onClick={() => setShowMailMergeTip(!showMailMergeTip)}
                    className="bg-blue-100 text-blue-700 font-bold px-3 py-2 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                    title="Show Mail Merge Steps"
                  >
                    ?
                  </button>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={generateOutlookScript}
                    disabled={parseStudentNames().length === 0 || parseStudentEmails().length === 0}
                    className="flex-1 bg-purple-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    📜 Download Outlook VBA Script
                  </button>
                  <button
                    onClick={() => setShowVbaTip(!showVbaTip)}
                    className="bg-purple-100 text-purple-700 font-bold px-3 py-2 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                    title="Show VBA Script Steps"
                  >
                    ?
                  </button>
                </div>

                <button
                  onClick={copyEmailsForBCC}
                  disabled={parseStudentEmails().filter(e => e.includes('@')).length === 0}
                  className="w-full bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm"
                >
                  📋 Copy All Emails (for BCC)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mail Merge Steps Popup Modal */}
      {showMailMergeTip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowMailMergeTip(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-yellow-800">💡 Word Mail Merge Steps</h3>
              <button onClick={() => setShowMailMergeTip(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li>Download all certificates to a folder</li>
              <li>Export Mail Merge CSV</li>
              <li>Open Word → Mailings → Start Mail Merge</li>
              <li>Select "E-mail Messages"</li>
              <li>Use "Select Recipients" → Use Existing List</li>
              <li>Insert merge fields in your email</li>
              <li>Finish & Merge → Send E-mail</li>
            </ol>
            <p className="mt-4 text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
              ⚠️ Note: Word mail merge doesn't support individual attachments. Use the Outlook VBA script for that.
            </p>
          </div>
        </div>
      )}

      {/* VBA Script Steps Popup Modal */}
      {showVbaTip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowVbaTip(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-purple-800">💻 Outlook VBA Steps</h3>
              <button onClick={() => setShowVbaTip(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li>Download all certificates to one folder</li>
              <li>Download the VBA script</li>
              <li>Open Outlook → Alt+F11</li>
              <li>Insert → Module → Paste script</li>
              <li>Edit the folder path in the script</li>
              <li>Press F5 to run</li>
            </ol>
            <p className="mt-4 text-sm text-purple-600 bg-purple-50 p-2 rounded">
              💡 Emails open in draft mode for review. Change .Display to .Send for auto-send.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchTool;
